// public-site/src/screens/Pay.js
// Card payment for a funeral case. Two paths:
//   1) Saved card on file (NMI Customer Vault) — one tap, no card entry.
//   2) New card via NMI Collect.js hosted fields (PAN never touches our server),
//      with an optional "save this card for future payments". Saving is OFF by
//      default: it is an explicit opt-in, and a payment must never fail only
//      because the gateway refused to store the card.
// Mirrors the Collect.js + vault pattern in the staff app's PhonePayment.js.

import React, { useEffect, useRef, useState } from 'react';
import { pay, formatUSD } from '../api';
import { styles, colors, SUPPORT_PHONE, MERCHANT_NAME } from '../theme';
import Brand from '../components/Brand';

const TOKENIZATION_KEY = process.env.REACT_APP_NMI_TOKENIZATION_KEY || '';

// "2026-07-01" → "July 1, 2026" (parsed as local date to avoid TZ drift).
function formatDueDate(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Container style for a Collect.js hosted field (the iframe fills it). Matches
// the translucent text inputs so the card form reads as one set on navy.
const fieldBox = {
  width: '100%',
  height: '56px',
  padding: '0 12px',
  borderRadius: '10px',
  border: `2px solid ${colors.inputBorder}`,
  background: colors.inputBg,
  marginBottom: '18px',
  display: 'flex',
  alignItems: 'center',
};

// Same list as the staff Phone Payment screen (public-site is a separate
// build and cannot import from src/).
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

// The state dropdown reuses the text-input look; the native arrow is replaced
// with a white chevron so it reads on the navy card.
const selectStyle = {
  ...styles.input,
  appearance: 'none',
  WebkitAppearance: 'none',
  paddingRight: '44px',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'14\' height=\'14\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23FFFFFF\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 16px center',
  cursor: 'pointer',
};

// Native option lists ignore most styling but do inherit color on some
// browsers; pin navy-on-white so the list never renders white-on-white.
const optionStyle = { color: colors.navyDeep, background: colors.white };

const linkButton = {
  background: 'none',
  border: 'none',
  color: colors.link,
  cursor: 'pointer',
  padding: '8px 0',
  fontSize: '16px',
  fontWeight: 600,
};

function formatCardLabel(card) {
  if (!card) return 'your saved card';
  const brand = (card.cardType || 'card').charAt(0).toUpperCase() + (card.cardType || 'card').slice(1);
  const last4 = card.cardLastFour ? ` ••••${card.cardLastFour}` : '';
  const exp = card.cardExpiration ? ` (exp ${card.cardExpiration})` : '';
  return `${brand}${last4}${exp}`;
}

export default function Pay({ caseInfo, onPaid, onBack }) {
  const balance = Number(caseInfo?.balance) || 0;
  const plan = caseInfo?.plan;
  const cardOnFile = caseInfo?.cardOnFile;
  const hasVault = !!(cardOnFile && cardOnFile.hasVault);

  const planActive = !!(plan && plan.status === 'Active');
  const installmentAmt =
    plan && plan.installmentAmount > 0 ? Math.min(plan.installmentAmount, balance) : 0;
  const halfAmt = balance >= 1 ? Math.round(balance * 50) / 100 : 0; // 50% of balance, to cents
  const freqLabel = String(plan?.frequency || '').toLowerCase();
  const nextDueLabel = formatDueDate(plan?.nextDueDate);

  // Quick-pay presets (the amount field stays editable for any custom amount).
  const presets = [];
  if (installmentAmt > 0) presets.push({ key: 'installment', label: 'Installment', value: installmentAmt });
  if (halfAmt > 0 && halfAmt < balance) presets.push({ key: 'half', label: 'Pay half', value: halfAmt });
  if (balance > 0) presets.push({ key: 'full', label: 'Pay full balance', value: balance });

  // Default the amount to the plan installment (capped at the balance) when a
  // plan exists; otherwise to the full balance.
  const suggested = installmentAmt > 0 ? installmentAmt : balance;

  const [amount, setAmount] = useState(suggested > 0 ? suggested.toFixed(2) : '');
  const [selectedPreset, setSelectedPreset] = useState(installmentAmt > 0 ? 'installment' : 'full');
  // The gateway's required-fields rule needs the cardholder's first and last
  // name as separate values (production refused "The Customer Name is required"
  // after address1 and city were satisfied). Same two fields the staff Phone
  // Payment screen sends; payerName (the Airtable Payer Name) is derived.
  const [cardFirstName, setCardFirstName] = useState('');
  const [cardLastName, setCardLastName] = useState('');
  const payerName = `${cardFirstName.trim()} ${cardLastName.trim()}`.trim();
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [useSaved, setUseSaved] = useState(hasVault);
  const [saveCard, setSaveCard] = useState(false);
  const [collectLoaded, setCollectLoaded] = useState(false);
  const [fieldsReady, setFieldsReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const configuredRef = useRef(false);
  const submittingRef = useRef(false);
  const callbackDataRef = useRef(null);

  // Load Collect.js once.
  useEffect(() => {
    if (window.CollectJS) {
      setCollectLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://secure.nmi.com/token/Collect.js';
    script.setAttribute('data-tokenization-key', TOKENIZATION_KEY);
    script.setAttribute('data-variant', 'inline');
    script.async = true;
    script.onload = () => setCollectLoaded(true);
    script.onerror = () => setError('We could not load the secure card form. Please refresh and try again.');
    document.head.appendChild(script);
  }, []);

  // Configure hosted fields once Collect.js is available AND we are entering a
  // new card (not when using the saved card — the fields aren't rendered then).
  useEffect(() => {
    if (useSaved || !collectLoaded || configuredRef.current || !window.CollectJS) return;
    configuredRef.current = true;

    try {
      window.CollectJS.configure({
        variant: 'inline',
        styleSniffer: false,
        fields: {
          ccnumber: { selector: '#cc-number', placeholder: 'Card number', title: 'Card Number' },
          ccexp: { selector: '#cc-exp', placeholder: 'MM / YY', title: 'Expiration' },
          cvv: { selector: '#cc-cvv', placeholder: 'CVV', title: 'CVV' },
        },
        // Card inputs live in NMI-hosted iframes. Their default white input
        // background is what made them read as "white pills" on the navy card —
        // force it transparent so the translucent field container shows through
        // and the three card fields match the rest of the form.
        customCss: { 'font-size': '18px', color: '#FFFFFF', 'background-color': 'transparent' },
        invalidCss: { color: '#FECACA' },
        placeholderCss: { color: 'rgba(255,255,255,0.55)', 'font-size': '18px' },
        focusCss: { color: '#FFFFFF', 'background-color': 'transparent' },
        fieldsAvailableCallback: () => setFieldsReady(true),
        validationCallback: (field, status, message) => {
          if (!status && submittingRef.current) {
            setError(`Please check your card details: ${message}`);
            setSubmitting(false);
            submittingRef.current = false;
          }
        },
        callback: (response) => {
          finishPayment(response);
        },
      });
    } catch (err) {
      setError('We could not initialize the secure card form. Please refresh and try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectLoaded, useSaved]);

  const enteredAmount = parseFloat(amount);
  const amountValid = !isNaN(enteredAmount) && enteredAmount > 0 && enteredAmount <= balance + 0.0001;

  const canSubmitNew =
    fieldsReady &&
    amountValid &&
    cardFirstName.trim().length > 0 &&
    cardLastName.trim().length > 0 &&
    // The gateway's address verification requires the full billing address
    // (a token sale missing address1, city, or state is refused before the card
    // is evaluated; production refused address1, then city, then the customer
    // name, in turn). Same six fields the staff Phone Payment screen requires.
    billingAddress.trim().length > 0 &&
    billingCity.trim().length > 0 &&
    billingState.length === 2 &&
    /^\d{5}$/.test(billingZip.trim()) &&
    !submitting;
  const canSubmitSaved = amountValid && !submitting;

  // New-card path: Collect.js tokenizes, then this fires with the token.
  async function finishPayment(response) {
    const token = response && response.token;
    if (!token) {
      setError('We could not read your card. Please try again.');
      setSubmitting(false);
      submittingRef.current = false;
      return;
    }
    const data = callbackDataRef.current || {};
    const maskedNumber = (response.card && response.card.number) || '';
    const cardLast4 = maskedNumber.replace(/\D/g, '').slice(-4);
    const cardType = (response.card && response.card.type) || '';
    const rawExp = (response.card && response.card.exp) || '';
    const cardExpiration = rawExp.length === 4 ? `${rawExp.slice(0, 2)}/${rawExp.slice(2)}` : rawExp;

    try {
      const result = await pay({
        amount: data.amount,
        paymentToken: token,
        payerName: data.payerName,
        cardFirstName: data.cardFirstName,
        cardLastName: data.cardLastName,
        billingAddress: data.billingAddress,
        billingCity: data.billingCity,
        billingState: data.billingState,
        billingZip: data.billingZip,
        cardLast4,
        cardType,
        cardExpiration,
        saveCard: !!data.saveCard,
        recurringAuthorized: !!data.recurringAuthorized,
      });
      onPaid({ ...result, amount: data.amount, payerName: data.payerName });
    } catch (err) {
      // The gateway refused to STORE the card (Customer Vault), not the card
      // itself: nothing was charged, and paying without saving will go through.
      // Say exactly that instead of "not approved", which sends a family off to
      // re-enter a card that was never the problem.
      const body = (err && err.data) || {};
      const vaultRefused =
        data.saveCard && body.isDecline === false && /vault/i.test(String(body.nmiResponseText || ''));
      // isDecline === false with a response code means the gateway ANSWERED
      // with an error (NMI response 3) before evaluating the card — a missing
      // required field, a routing or configuration problem — not a bank
      // decline. "Not approved" would send the family off to try another card
      // that was never the problem; say that nothing was charged and what to
      // check instead. A transport failure (no response code) keeps the
      // server's copy, because "nothing was charged" is not provable there.
      const refusedBeforeCard = body.isDecline === false && !!body.nmiResponseCode;
      setError(
        vaultRefused
          ? `We could not save your card, so no payment was made. Please uncheck "Save this card" and try again, or call ${SUPPORT_PHONE}.`
          : refusedBeforeCard
            ? `The card processor could not accept this payment as entered, so nothing was charged. Please check the name and billing address, or call ${SUPPORT_PHONE}.`
            : err.message
      );
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  const handlePayNew = () => {
    if (!canSubmitNew) return;
    setError('');
    callbackDataRef.current = {
      amount: parseFloat(amount),
      payerName,
      cardFirstName: cardFirstName.trim(),
      cardLastName: cardLastName.trim(),
      billingAddress: billingAddress.trim(),
      billingCity: billingCity.trim(),
      billingState,
      billingZip: billingZip.trim(),
      saveCard,
      // Saving a card while on an active plan enrolls in (and authorizes) auto-pay.
      recurringAuthorized: planActive && saveCard,
    };
    setSubmitting(true);
    submittingRef.current = true;
    try {
      window.CollectJS.startPaymentRequest();
    } catch (err) {
      setError('We could not start the payment. Please try again.');
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  // Saved-card path: no Collect.js, charge the vaulted card directly.
  const handlePaySaved = async () => {
    if (!canSubmitSaved) return;
    setError('');
    setSubmitting(true);
    submittingRef.current = true;
    try {
      const result = await pay({
        amount: parseFloat(amount),
        payerName: payerName || undefined,
        useVaultId: true,
      });
      onPaid({ ...result, amount: parseFloat(amount), payerName });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const switchToNewCard = () => {
    configuredRef.current = false;
    setFieldsReady(false);
    setUseSaved(false);
  };

  const choosePreset = (preset) => {
    setSelectedPreset(preset.key);
    setAmount(Number(preset.value).toFixed(2));
  };

  const presetButtonStyle = (active) => ({
    flex: '1 1 140px',
    minHeight: '60px',
    borderRadius: '12px',
    padding: '10px 14px',
    fontFamily: "'Host Grotesk', -apple-system, sans-serif",
    fontSize: '18px',
    fontWeight: 700,
    cursor: 'pointer',
    background: active ? colors.gold : 'transparent',
    color: active ? colors.navyDeep : colors.white,
    border: `2px solid ${active ? colors.gold : colors.inputBorder}`,
  });

  // Calm, dignified disclosure text shown beneath the save-card / plan controls.
  const disclosureStyle = {
    ...styles.bodyText,
    fontSize: '16px',
    lineHeight: 1.5,
    color: colors.textSecondary,
    margin: '0 0 18px',
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Brand tagline="Make a payment" />

        <h1 style={styles.title}>Payment</h1>
        <p style={styles.bodyText}>
          Balance due: <strong>{formatUSD(balance)}</strong>. You may pay the full amount or a
          portion.
        </p>

        {error && <div style={styles.errorBox}>{error}</div>}

        {presets.length > 0 && (
          <>
            <label style={styles.label}>Choose an amount</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
              {presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  style={presetButtonStyle(selectedPreset === p.key)}
                  onClick={() => choosePreset(p)}
                >
                  {p.label}<br />
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>{formatUSD(p.value)}</span>
                </button>
              ))}
              <button
                type="button"
                style={presetButtonStyle(selectedPreset === 'custom')}
                onClick={() => setSelectedPreset('custom')}
              >
                Other amount
              </button>
            </div>
          </>
        )}

        <label style={styles.label} htmlFor="amount">Amount to pay</label>
        <input
          id="amount"
          style={styles.input}
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => { setAmount(e.target.value.replace(/[^0-9.]/g, '')); setSelectedPreset('custom'); }}
        />
        {!amountValid && amount !== '' && (
          <p style={{ ...styles.errorBox, marginTop: '-10px' }}>
            Please enter an amount between $0.01 and {formatUSD(balance)}.
          </p>
        )}

        {useSaved ? (
          <>
            <div
              style={{
                background: colors.nested,
                border: `1px solid ${colors.hairline}`,
                borderRadius: '12px',
                padding: '16px 18px',
                marginBottom: '18px',
              }}
            >
              <div style={{ ...styles.label, marginBottom: '6px' }}>Card on file</div>
              <div style={{ ...styles.bodyText, margin: 0, color: colors.textPrimary }}>
                {formatCardLabel(cardOnFile)}
              </div>
            </div>

            {planActive && balance > 0 && (
              <p style={disclosureStyle}>
                Automatic plan payments of {formatUSD(installmentAmt || plan.installmentAmount)} {freqLabel} are
                scheduled on your saved card{nextDueLabel ? `. Next: ${nextDueLabel}` : ''}. Call {SUPPORT_PHONE} to
                make changes.
              </p>
            )}

            <button style={styles.buttonPrimary} onClick={handlePaySaved} disabled={!canSubmitSaved}>
              {submitting ? 'Processing…' : `Pay ${amountValid ? formatUSD(enteredAmount) : ''}`.trim()}
            </button>

            <button style={{ ...linkButton, display: 'block', width: '100%', marginTop: '12px' }} onClick={switchToNewCard} disabled={submitting}>
              Use a different card
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '14px' }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label} htmlFor="cardFirstName">First name on card</label>
                <input
                  id="cardFirstName"
                  style={styles.input}
                  type="text"
                  autoComplete="cc-given-name"
                  value={cardFirstName}
                  onChange={(e) => setCardFirstName(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label} htmlFor="cardLastName">Last name on card</label>
                <input
                  id="cardLastName"
                  style={styles.input}
                  type="text"
                  autoComplete="cc-family-name"
                  value={cardLastName}
                  onChange={(e) => setCardLastName(e.target.value)}
                />
              </div>
            </div>

            <label style={styles.label}>Card number</label>
            <div id="cc-number" style={fieldBox} />

            <div style={{ display: 'flex', gap: '14px' }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Expiration</label>
                <div id="cc-exp" style={fieldBox} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>CVV</label>
                <div id="cc-cvv" style={fieldBox} />
              </div>
            </div>

            <label style={styles.label} htmlFor="billingAddress">Billing street address</label>
            <input
              id="billingAddress"
              style={styles.input}
              type="text"
              autoComplete="address-line1"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
            />

            <div style={{ display: 'flex', gap: '14px' }}>
              <div style={{ flex: '1 1 60%' }}>
                <label style={styles.label} htmlFor="billingCity">Billing city</label>
                <input
                  id="billingCity"
                  style={styles.input}
                  type="text"
                  autoComplete="address-level2"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 40%' }}>
                <label style={styles.label} htmlFor="billingState">State</label>
                <select
                  id="billingState"
                  style={selectStyle}
                  autoComplete="address-level1"
                  value={billingState}
                  onChange={(e) => setBillingState(e.target.value)}
                >
                  <option value="" style={optionStyle}>Select</option>
                  {US_STATES.map((st) => (
                    <option key={st} value={st} style={optionStyle}>{st}</option>
                  ))}
                </select>
              </div>
            </div>

            <label style={styles.label} htmlFor="zip">Billing ZIP code</label>
            <input
              id="zip"
              style={styles.input}
              type="text"
              inputMode="numeric"
              maxLength={5}
              autoComplete="postal-code"
              value={billingZip}
              onChange={(e) => setBillingZip(e.target.value.replace(/\D/g, ''))}
            />

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                marginBottom: '18px',
              }}
            >
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                style={{ width: '22px', height: '22px', accentColor: colors.goldStrong, cursor: 'pointer' }}
              />
              <span style={{ ...styles.bodyText, margin: 0, fontSize: '16px' }}>
                {planActive
                  ? 'Save my card and authorize automatic plan payments'
                  : 'Save this card for future payments'}
              </span>
            </label>

            {planActive ? (
              saveCard ? (
                <p style={disclosureStyle}>
                  You're on a payment plan of {formatUSD(installmentAmt || plan.installmentAmount)} {freqLabel}. By
                  checking this and continuing, you authorize {MERCHANT_NAME} to automatically charge this card{' '}
                  {formatUSD(installmentAmt || plan.installmentAmount)} on each due date
                  {nextDueLabel ? ` — next on ${nextDueLabel}` : ''}, until your balance of {formatUSD(balance)} is paid
                  in full. We'll email a receipt for each charge. You can pay ahead or in full anytime, or call{' '}
                  {SUPPORT_PHONE} to make changes.
                </p>
              ) : (
                <p style={disclosureStyle}>
                  You're on a payment plan of {formatUSD(installmentAmt || plan.installmentAmount)} {freqLabel}. These
                  are not automatic — please pay each installment here or at the funeral home by each due date
                  {nextDueLabel ? ` (next due ${nextDueLabel})` : ''}. Leaving this box unchecked keeps you on the plan;
                  it does not remove you from it.
                </p>
              )
            ) : (
              <p style={disclosureStyle}>Your card will not be charged automatically.</p>
            )}

            {!fieldsReady && (
              <p style={{ ...styles.bodyText, color: colors.textMuted, fontSize: '16px' }}>
                Loading secure card fields…
              </p>
            )}

            <button style={styles.buttonPrimary} onClick={handlePayNew} disabled={!canSubmitNew}>
              {submitting ? 'Processing…' : `Pay ${amountValid ? formatUSD(enteredAmount) : ''}`.trim()}
            </button>

            {hasVault && (
              <button style={{ ...linkButton, display: 'block', width: '100%', marginTop: '12px' }} onClick={() => setUseSaved(true)} disabled={submitting}>
                Use saved card instead
              </button>
            )}
          </>
        )}

        <button
          style={{ ...styles.buttonSecondary, marginTop: '14px', border: 'none', color: colors.textMuted }}
          onClick={onBack}
          disabled={submitting}
        >
          Back
        </button>
      </div>
    </div>
  );
}
